/**
 * Hook that loads the DSID (dataset/field) catalog for the report builder.
 *
 * The catalog is fetched once per session and cached in module scope so
 * every builder instance reuses the same data. Re-mounts incur no network
 * call after the first successful fetch.
 */

import { useEffect, useState } from 'react';
import {
  reportingAPI,
  type DsidCatalogResponse,
} from '../../../lib/api/reporting';

let cachedCatalog: DsidCatalogResponse | null = null;
let inflight: Promise<DsidCatalogResponse> | null = null;

interface UseDsidCatalogResult {
  catalog: DsidCatalogResponse | null;
  loading: boolean;
}

/**
 * Returns the DSID catalog (dataset list with fields) used by the builder.
 *
 * @returns `{ catalog, loading }` — `catalog` is `null` until the first
 *   response arrives. Subsequent consumers receive the cached value
 *   synchronously.
 */
export function useDsidCatalog(): UseDsidCatalogResult {
  const [catalog, setCatalog] = useState<DsidCatalogResponse | null>(cachedCatalog);
  const [loading, setLoading] = useState<boolean>(cachedCatalog === null);

  useEffect(() => {
    if (cachedCatalog) {
      setCatalog(cachedCatalog);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    if (!inflight) {
      inflight = reportingAPI
        .getDsidCatalog()
        .then((res) => {
          cachedCatalog = res.data;
          return res.data;
        })
        .finally(() => {
          inflight = null;
        });
    }
    inflight
      .then((data) => {
        if (!cancelled) {
          setCatalog(data);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { catalog, loading };
}
